import { useState, useEffect, useCallback } from 'react';
import {
  getActiveCoverLetter,
  getAllCoverLetters,
  saveOrUpdateActiveCoverLetter,
  deleteCoverLetter,
  setActiveCoverLetter,
  type CoverLetter,
} from '../lib/supabase/coverLetters';
import { useAuth } from '../contexts/AuthContext';

export function useCoverLetter() {
  const { user } = useAuth();
  const [activeCoverLetter, setActiveCoverLetter] = useState<CoverLetter | null>(null);
  const [allCoverLetters, setAllCoverLetters] = useState<CoverLetter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadActiveCoverLetter = useCallback(async () => {
    if (!user) {
      setActiveCoverLetter(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const coverLetter = await getActiveCoverLetter(user.id);
      setActiveCoverLetter(coverLetter);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cover letter');
      console.error('Error loading active cover letter:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const loadAllCoverLetters = useCallback(async () => {
    if (!user) {
      setAllCoverLetters([]);
      return;
    }

    try {
      const coverLetters = await getAllCoverLetters(user.id);
      setAllCoverLetters(coverLetters);
    } catch (err) {
      console.error('Error loading all cover letters:', err);
    }
  }, [user]);

  useEffect(() => {
    loadActiveCoverLetter();
  }, [loadActiveCoverLetter]);

  const saveOrUpdate = useCallback(
    async (content: string): Promise<boolean> => {
      if (!user) {
        setError('You must be logged in to save a cover letter');
        return false;
      }

      if (!content.trim()) {
        setError('Cover letter content cannot be empty');
        return false;
      }

      try {
        setError(null);
        const savedCoverLetter = await saveOrUpdateActiveCoverLetter(user.id, content);
        setActiveCoverLetter(savedCoverLetter);
        await loadAllCoverLetters();
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save cover letter');
        console.error('Error saving cover letter:', err);
        return false;
      }
    },
    [user, loadAllCoverLetters]
  );

  const deleteCoverLetterById = useCallback(
    async (id: string): Promise<boolean> => {
      if (!user) {
        setError('You must be logged in to delete a cover letter');
        return false;
      }

      try {
        setError(null);
        await deleteCoverLetter(id, user.id);

        if (activeCoverLetter?.id === id) {
          setActiveCoverLetter(null);
        }

        await loadAllCoverLetters();
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete cover letter');
        console.error('Error deleting cover letter:', err);
        return false;
      }
    },
    [user, activeCoverLetter, loadAllCoverLetters]
  );

  const setActive = useCallback(
    async (id: string): Promise<boolean> => {
      if (!user) {
        setError('You must be logged in to set active cover letter');
        return false;
      }

      try {
        setError(null);
        const updatedCoverLetter = await setActiveCoverLetter(id, user.id);
        setActiveCoverLetter(updatedCoverLetter);
        await loadAllCoverLetters();
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to set active cover letter');
        console.error('Error setting active cover letter:', err);
        return false;
      }
    },
    [user, loadAllCoverLetters]
  );

  return {
    activeCoverLetter,
    allCoverLetters,
    loading,
    error,
    saveOrUpdate,
    deleteCoverLetter: deleteCoverLetterById,
    setActiveCoverLetter: setActive,
    refresh: loadActiveCoverLetter,
    loadAllCoverLetters,
  };
}
